"use strict";
/**
 * Idempotency for client-emitted user events.
 *
 * THE PROBLEM. A single real sign-in is observed by several independent code
 * paths, in separate documents, across app launches:
 *   - the OAuth popup's callback page AND the opener's success handler
 *   - the popup-close rescue refetch (CRE-783) adopting the same session
 *   - React StrictMode double-invoking effects in dev
 *   - a user reloading / bookmarking the `/sign-in/callback?...` URL
 *   - mobile's in-process `openAuthSessionAsync` path AND the cold
 *     `auth/callback` deep-link route
 * `useRef` guards cannot cover any of this: a ref is per-document and dies on
 * reload.
 *
 * THE FIX. Key the dedupe on the TOKEN MINT rather than on the code path. The
 * backend issues exactly one access token per authentication, and every path
 * above observes that SAME token string. So a fingerprint of the token is a
 * stable, path-independent identity for "this one sign-in", and all of the cases
 * above collapse onto one key.
 *
 * This is also why the rescue paths need no special-casing: a rescue-adopted
 * session carries the same token, hence the same key. Safe by construction
 * rather than by discipline.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseDedupeState = exports.releaseKey = exports.claimKey = exports.buildSkimDedupeKey = exports.buildAuthDedupeKey = exports.fingerprintToken = exports.SKIM_DEDUPE_BUCKET_MS = exports.DEDUPE_MAX_ENTRIES = exports.DEDUPE_TTL_MS = void 0;
/** Bump when the key format changes, so old entries can't collide with new. */
const KEY_VERSION = 'v1';
const KEY_PREFIX = `ue:${KEY_VERSION}`;
/** Entries older than this are pruned on the next write. */
exports.DEDUPE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * Hard ceiling on stored entries so client storage can never grow unbounded
 * (same defensive-cap reasoning as the brand-slug cache in the website's
 * middleware). When full, the oldest entries are evicted first.
 */
exports.DEDUPE_MAX_ENTRIES = 50;
/**
 * Default bucket for the repeatable SHARE_SKIM action. Long enough to swallow a
 * double-submit or a retry re-entering the request path, short enough that a
 * genuine re-analysis of the same video later still records.
 */
exports.SKIM_DEDUPE_BUCKET_MS = 60_000;
/**
 * 32-bit FNV-1a. Two passes with different offset bases are concatenated to get
 * a 16-hex-char fingerprint — enough entropy that a collision inside a
 * 50-entry store is not a practical concern, and a collision would merely
 * suppress one duplicate event.
 *
 * Why fingerprint instead of decoding the JWT: no base64url/UTF-8 decoding
 * (which is not uniformly available across Next.js, browsers and Metro), no
 * assumptions about payload shape, and the result is one-way — the stored key
 * reveals nothing about the token.
 */
const fnv1a = (input, offsetBasis) => {
    let hash = offsetBasis;
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        // 16777619 (FNV prime) via shifts to stay in 32-bit range.
        hash +=
            (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
        hash >>>= 0;
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
};
/**
 * Stable, non-reversible fingerprint of an access token. Returns null for an
 * absent/empty token so callers can skip rather than dedupe on a constant.
 */
const fingerprintToken = (token) => {
    if (typeof token !== 'string' || token.length === 0)
        return null;
    return `${fnv1a(token, 0x811c9dc5)}${fnv1a(token, 0x01000193)}`;
};
exports.fingerprintToken = fingerprintToken;
/**
 * Dedupe key for an authentication event.
 *
 * DELIBERATELY EXCLUDES the event type. One token mint is ONE authentication,
 * and that authentication is either a signup or a sign-in — never both. Keying
 * on the type would let two emitters that disagree about the type each claim
 * their own key and both write: exactly what happens when a new user signs in
 * through the popup (the callback page knows `is_new_user=true` and emits
 * SIGN_UP, while the opener's safety-net path cannot see that param and would
 * emit SIGN_IN). Type-free keying makes the first writer win and the second a
 * no-op, which is the correct outcome — the popup is also the better-informed
 * emitter, so the type it records is the accurate one.
 *
 * Returns null when the token cannot be fingerprinted — the caller must then
 * skip the emit rather than fall back to a weaker key (a token-less key would
 * dedupe ACROSS different sign-ins and silently drop real events).
 */
const buildAuthDedupeKey = (input) => {
    const fingerprint = (0, exports.fingerprintToken)(input.accessToken);
    if (!fingerprint)
        return null;
    return `${KEY_PREFIX}:AUTH:${input.userId}:${fingerprint}`;
};
exports.buildAuthDedupeKey = buildAuthDedupeKey;
/**
 * Dedupe key for the repeatable on-demand-analysis action. Time-bucketed
 * instead of token-keyed: the same user analysing the same video twice in the
 * same minute is a double-fire, but doing it an hour later is two real actions.
 */
const buildSkimDedupeKey = (input) => {
    const bucketMs = input.bucketMs && input.bucketMs > 0
        ? input.bucketMs
        : exports.SKIM_DEDUPE_BUCKET_MS;
    const bucket = Math.floor(input.nowMs / bucketMs);
    return `${KEY_PREFIX}:SHARE_SKIM:${input.userId}:${input.videoId}:${bucket}`;
};
exports.buildSkimDedupeKey = buildSkimDedupeKey;
/**
 * Pure claim-and-prune. Kept side-effect free so both the sync (localStorage)
 * and async (AsyncStorage) adapters share one tested implementation.
 *
 * Claims BEFORE the network call by design: releasing on failure is cheap
 * (`releaseKey`), whereas claiming after the response leaves a window where two
 * paths both pass the check and both post.
 */
const claimKey = (state, key, nowMs) => {
    const current = state && typeof state === 'object' ? state : {};
    // Prune BEFORE the already-claimed check, so an expired claim is re-claimable
    // rather than blocking forever. (Checking first would make the TTL
    // meaningless for the very key it applies to.)
    const pruned = {};
    for (const [existingKey, claimedAt] of Object.entries(current)) {
        if (typeof claimedAt !== 'number')
            continue;
        if (nowMs - claimedAt >= exports.DEDUPE_TTL_MS)
            continue;
        pruned[existingKey] = claimedAt;
    }
    if (typeof pruned[key] === 'number') {
        return { claimed: false, next: current };
    }
    // Still over the cap after TTL pruning: evict oldest-claimed first, leaving
    // room for the incoming key.
    const entries = Object.entries(pruned);
    if (entries.length >= exports.DEDUPE_MAX_ENTRIES) {
        entries.sort((a, b) => a[1] - b[1]);
        const overflow = entries.length - exports.DEDUPE_MAX_ENTRIES + 1;
        for (let i = 0; i < overflow; i += 1) {
            const entry = entries[i];
            if (entry)
                delete pruned[entry[0]];
        }
    }
    pruned[key] = nowMs;
    return { claimed: true, next: pruned };
};
exports.claimKey = claimKey;
/**
 * Drop a claim so a later attempt can retry. Use ONLY for retryable failures
 * (5xx, network, timeout). A 4xx will fail identically on retry, so keeping
 * that claim is what stops a permanent failure from looping forever.
 */
const releaseKey = (state, key) => {
    const current = state && typeof state === 'object' ? state : {};
    if (!(key in current))
        return current;
    const next = { ...current };
    delete next[key];
    return next;
};
exports.releaseKey = releaseKey;
/**
 * Parse persisted JSON into a `DedupeState`, tolerating corruption. A bad blob
 * resolves to an empty state (fail-open toward "not yet claimed") rather than
 * throwing into the auth path.
 */
const parseDedupeState = (raw) => {
    if (typeof raw !== 'string' || raw.length === 0)
        return {};
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }
        const state = {};
        for (const [key, value] of Object.entries(parsed)) {
            if (typeof value === 'number' && Number.isFinite(value)) {
                state[key] = value;
            }
        }
        return state;
    }
    catch {
        return {};
    }
};
exports.parseDedupeState = parseDedupeState;
